// Iter 83.4 / Phase E4 Sprint 4 — GL Export Console.
// Select source (payroll_run / fnf / loan_disbursement / asset_writeoff),
// provider (tally / zoho), preview vouchers, export (persists D29
// snapshot), and re-download historical exports byte-identically.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    ArrowsClockwise, DownloadSimple, Eye, FileText, MagnifyingGlass,
    CheckCircle, XCircle, ShieldCheck, ListChecks,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

const SOURCE_LABEL = {
    payroll_run: "Payroll Run",
    fnf: "FNF Settlement",
    loan_disbursement: "Loan Disbursement",
    asset_writeoff: "Asset Write-off",
};

const fmtMoney = (n) =>
    `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const truncHash = (h) => (h ? `${h.slice(0, 12)}…${h.slice(-8)}` : "");

export default function GlExportConsole() {
    const { can } = useAuth();
    const canExport = can("gl:admin");
    const [provider, setProvider] = useState("tally");
    const [sourceType, setSourceType] = useState("fnf");
    const [eligible, setEligible] = useState([]);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [search, setSearch] = useState("");
    // Selection
    const [selectedSourceId, setSelectedSourceId] = useState("");
    const [preview, setPreview] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [verifyOpen, setVerifyOpen] = useState(false);
    const [verifyResult, setVerifyResult] = useState(null);

    const loadEligible = useCallback(async () => {
        try {
            const { data } = await api.get(`/gl/sources/eligible?source_type=${sourceType}&limit=100`);
            setEligible(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load eligible sources"));
        }
    }, [sourceType]);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get(`/gl/exports?limit=100`);
            setHistory(data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load history"));
        }
    }, []);

    useEffect(() => { loadEligible(); }, [loadEligible]);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    const changeSourceType = (next) => {
        setSourceType(next);
        setSelectedSourceId("");
    };

    const previewBuild = async () => {
        if (!selectedSourceId) {
            toast.error("Select a source first");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/gl/preview", {
                source_type: sourceType,
                source_id: selectedSourceId,
                provider,
            });
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Preview failed"));
        } finally {
            setLoading(false);
        }
    };

    const exportRun = async () => {
        if (!selectedSourceId) {
            toast.error("Select a source first");
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post("/gl/export", {
                source_type: sourceType,
                source_id: selectedSourceId,
                provider,
                note: `${SOURCE_LABEL[sourceType]} export via console`,
            });
            toast.success(`Export ${data.id} created · ${data.n_vouchers} voucher(s)`);
            // Trigger download
            downloadExport(data.id, data.file_name);
            await loadHistory();
        } catch (err) {
            toast.error(formatApiError(err, "Export failed"));
        } finally {
            setLoading(false);
        }
    };

    const downloadExport = async (exportId, filename) => {
        try {
            const r = await api.get(`/gl/exports/${exportId}/download`, { responseType: "blob" });
            const blob = new Blob([r.data], { type: r.headers["content-type"] });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename || `${exportId}.dat`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(formatApiError(err, "Download failed"));
        }
    };

    const verifyExport = async (exportId) => {
        try {
            const { data } = await api.post(`/gl/exports/${exportId}/verify`);
            setVerifyResult(data);
            setVerifyOpen(true);
        } catch (err) {
            toast.error(formatApiError(err, "Verify failed"));
        }
    };

    const renderEligibleLabel = (s) => {
        if (sourceType === "payroll_run")
            return `${s.id} · ${s.label || ""} · ${s.period_start || ""} → ${s.period_end || ""} · ${s.status}`;
        if (sourceType === "fnf")
            return `${s.id} · ${s.employee_name_snapshot || s.employee_id} · ${s.exit_type || ""} · LWD ${s.lwd || ""} · ${fmtMoney(s.net_settlement_amount)}`;
        if (sourceType === "loan_disbursement")
            return `${s.id} · ${s.employee_name_snapshot || s.employee_id} · ${s.policy_code_snapshot || ""} · ${fmtMoney(s.principal)} · ${s.status}`;
        if (sourceType === "asset_writeoff")
            return `${s.id} · ${s.asset_tag} · ${s.category_name_snapshot || ""} · ${fmtMoney(s.depreciated_value)}`;
        return s.id;
    };

    const filteredHistory = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return history;
        return history.filter((h) =>
            h.id.toLowerCase().includes(q) ||
            (h.source_id || "").toLowerCase().includes(q) ||
            (h.source_type || "").toLowerCase().includes(q) ||
            (h.provider || "").toLowerCase().includes(q),
        );
    }, [history, search]);

    return (
        <div className="space-y-4" data-testid="gl-export-console">
            <PageHeader
                title="GL Export Console"
                subtitle="Build deterministic Tally XML / Zoho CSV journals from payroll, FNF, loans, and asset write-offs. Every export persists a 4-hash D29 snapshot for forensic reproducibility."
                actions={
                    <Button variant="outline" size="sm" onClick={() => { loadEligible(); loadHistory(); }} data-testid="gl-refresh-btn">
                        <ArrowsClockwise size={14} className="mr-1" /> Refresh
                    </Button>
                }
            />

            {/* Builder panel */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border rounded p-4 space-y-3 bg-slate-50/40">
                    <h3 className="font-semibold text-sm">Build New Export</h3>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs">Provider</Label>
                            <select
                                value={provider}
                                onChange={(e) => setProvider(e.target.value)}
                                className="w-full h-8 border rounded px-2 text-sm"
                                data-testid="gl-export-provider"
                            >
                                <option value="tally">Tally India (XML)</option>
                                <option value="zoho">Zoho Books (CSV)</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs">Source Type</Label>
                            <select
                                value={sourceType}
                                onChange={(e) => changeSourceType(e.target.value)}
                                className="w-full h-8 border rounded px-2 text-sm"
                                data-testid="gl-export-source-type"
                            >
                                <option value="payroll_run">Payroll Run</option>
                                <option value="fnf">FNF Settlement</option>
                                <option value="loan_disbursement">Loan Disbursement</option>
                                <option value="asset_writeoff">Asset Write-off</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">Source Record</Label>
                        <select
                            value={selectedSourceId}
                            onChange={(e) => setSelectedSourceId(e.target.value)}
                            className="w-full h-8 border rounded px-2 text-sm"
                            data-testid="gl-export-source-id"
                        >
                            <option value="">Select eligible {SOURCE_LABEL[sourceType]}…</option>
                            {eligible.map((s) => (
                                <option key={s.id} value={s.id}>{renderEligibleLabel(s)}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-slate-500 mt-1">
                            Showing {eligible.length} eligible record(s) for the selected source type.
                        </p>
                    </div>
                    <div className="flex gap-2 pt-2">
                        <Button
                            variant="outline" size="sm"
                            disabled={!selectedSourceId || loading}
                            onClick={previewBuild}
                            data-testid="gl-preview-btn"
                        >
                            <Eye size={14} className="mr-1" /> Preview Vouchers
                        </Button>
                        {canExport && (
                            <Button
                                size="sm"
                                disabled={!selectedSourceId || loading}
                                onClick={exportRun}
                                data-testid="gl-export-run-btn"
                            >
                                <DownloadSimple size={14} className="mr-1" /> Export & Freeze
                            </Button>
                        )}
                    </div>
                </div>

                <div className="border rounded p-4 space-y-2 bg-slate-50/40 text-xs">
                    <h3 className="font-semibold text-sm">Sprint 4 / D29 — Reproducibility Guarantees</h3>
                    <ul className="list-disc pl-5 space-y-1">
                        <li>Every export persists <span className="font-mono">source_record_hash</span> · <span className="font-mono">mapping_snapshot_hash</span> · <span className="font-mono">export_payload_hash</span> · <span className="font-mono">file_bytes_hash</span>.</li>
                        <li>Re-downloading a historical export returns <strong>byte-identical</strong> bytes (re-verified against the hash on each read).</li>
                        <li>Mapping changes <strong>do not</strong> invalidate prior exports — they live in their own snapshot.</li>
                        <li>Unmapped components abort the export with a named error pointing to GL Mappings.</li>
                    </ul>
                </div>
            </div>

            {/* History panel */}
            <div className="border rounded">
                <div className="flex items-center gap-2 p-3 border-b bg-slate-50">
                    <h3 className="font-semibold text-sm flex-1">Export History</h3>
                    <div className="relative max-w-xs">
                        <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search exports…"
                            className="pl-8 h-8 text-sm"
                            data-testid="gl-history-search"
                        />
                    </div>
                </div>
                <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b uppercase tracking-wide text-slate-600">
                        <tr>
                            <th className="px-3 py-2 text-left">Export ID</th>
                            <th className="px-3 py-2 text-left">Source</th>
                            <th className="px-3 py-2 text-left">Provider</th>
                            <th className="px-3 py-2 text-left">Generated</th>
                            <th className="px-3 py-2 text-left">By</th>
                            <th className="px-3 py-2 text-left">Mapping Ver</th>
                            <th className="px-3 py-2 text-left">Bytes Hash</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredHistory.length === 0 ? (
                            <tr><td colSpan={8} className="text-center py-6 text-slate-400">No exports yet</td></tr>
                        ) : (
                            filteredHistory.map((h) => (
                                <tr key={h.id} className="border-b hover:bg-slate-50/50" data-testid={`gl-history-row-${h.id}`}>
                                    <td className="px-3 py-2 font-mono">{h.id}</td>
                                    <td className="px-3 py-2">
                                        <Badge className="bg-slate-100 text-slate-700 text-[10px]">
                                            {SOURCE_LABEL[h.source_type] || h.source_type}
                                        </Badge>
                                        <div className="font-mono text-[10px] text-slate-500 mt-0.5">{h.source_id}</div>
                                    </td>
                                    <td className="px-3 py-2 capitalize">{h.provider}</td>
                                    <td className="px-3 py-2 text-[11px]">{h.generated_at?.slice(0, 19).replace("T", " ")}</td>
                                    <td className="px-3 py-2 text-[11px]">{h.generated_by_email || h.generated_by_id}</td>
                                    <td className="px-3 py-2 font-mono">v{h.coa_mapping_version}</td>
                                    <td className="px-3 py-2 font-mono text-[10px]" title={h.file_bytes_hash}>
                                        {truncHash(h.file_bytes_hash)}
                                    </td>
                                    <td className="px-3 py-2 text-right space-x-1">
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => downloadExport(h.id, h.file_name)}
                                            data-testid={`gl-history-download-${h.id}`}
                                        >
                                            <DownloadSimple size={12} />
                                        </Button>
                                        <Button
                                            variant="outline" size="sm"
                                            onClick={() => verifyExport(h.id)}
                                            data-testid={`gl-history-verify-${h.id}`}
                                        >
                                            <ShieldCheck size={12} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Preview dialog */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>Voucher Preview · {preview && SOURCE_LABEL[preview.source_type]} · {preview?.provider}</DialogTitle>
                        <DialogDescription className="text-xs">
                            Read-only preview. No export run is persisted until you click Export &amp; Freeze.
                        </DialogDescription>
                    </DialogHeader>
                    {preview && (
                        <div className="space-y-2 text-xs">
                            <div className="flex gap-2">
                                <Badge className={preview.balanced ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}>
                                    {preview.balanced ? "✓ Balanced" : "✗ Not balanced"}
                                </Badge>
                                <Badge className="bg-slate-100 text-slate-700">{preview.n_vouchers} voucher(s)</Badge>
                            </div>
                            {preview.vouchers.map((v, idx) => (
                                <div key={idx} className="border rounded p-2 bg-white">
                                    <div className="font-semibold">{v.voucher_type} · {v.reference_number} · {v.voucher_date}</div>
                                    <div className="text-slate-500 mb-1">{v.narration}</div>
                                    <table className="w-full text-[11px]">
                                        <thead className="bg-slate-100">
                                            <tr>
                                                <th className="text-left px-2 py-1">Ledger</th>
                                                <th className="text-left px-2 py-1">Code</th>
                                                <th className="text-right px-2 py-1">Debit</th>
                                                <th className="text-right px-2 py-1">Credit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {v.entries.map((e, ei) => (
                                                <tr key={ei} className="border-b">
                                                    <td className="px-2 py-1">{e.ledger_name}</td>
                                                    <td className="px-2 py-1 font-mono">{e.component_code}</td>
                                                    <td className="px-2 py-1 text-right font-mono">{Number(e.debit) > 0 ? fmtMoney(e.debit) : ""}</td>
                                                    <td className="px-2 py-1 text-right font-mono">{Number(e.credit) > 0 ? fmtMoney(e.credit) : ""}</td>
                                                </tr>
                                            ))}
                                            <tr className="font-bold">
                                                <td colSpan={2} className="px-2 py-1 text-right">Total</td>
                                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(v.total_debit)}</td>
                                                <td className="px-2 py-1 text-right font-mono">{fmtMoney(v.total_credit)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
                        {canExport && (
                            <Button onClick={() => { setPreviewOpen(false); exportRun(); }}>
                                <DownloadSimple size={14} className="mr-1" /> Export & Freeze
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Verify dialog */}
            <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>D29 Reproducibility Verification</DialogTitle>
                        <DialogDescription className="text-xs">
                            Recomputes the 4 D29 hashes from the persisted snapshot and compares against the stored values.
                        </DialogDescription>
                    </DialogHeader>
                    {verifyResult && (
                        <div className="space-y-2 text-xs">
                            <div className="flex gap-2">
                                {verifyResult.all_match ? (
                                    <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle size={12} className="mr-1 inline" /> All hashes match</Badge>
                                ) : (
                                    <Badge className="bg-rose-100 text-rose-800"><XCircle size={12} className="mr-1 inline" /> MISMATCH</Badge>
                                )}
                            </div>
                            <table className="w-full">
                                <thead className="bg-slate-50">
                                    <tr>
                                        <th className="text-left px-2 py-1">Hash</th>
                                        <th className="text-left px-2 py-1">Stored</th>
                                        <th className="text-left px-2 py-1">Recomputed</th>
                                        <th className="text-center px-2 py-1">OK?</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.keys(verifyResult.stored).map((k) => (
                                        <tr key={k} className="border-b">
                                            <td className="px-2 py-1 font-semibold">{k}</td>
                                            <td className="px-2 py-1 font-mono text-[10px]" title={verifyResult.stored[k]}>{truncHash(verifyResult.stored[k])}</td>
                                            <td className="px-2 py-1 font-mono text-[10px]" title={verifyResult.recomputed[k]}>{truncHash(verifyResult.recomputed[k])}</td>
                                            <td className="px-2 py-1 text-center">
                                                {verifyResult.matches[k.replace("_hash", "")] ? "✓" : "✗"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setVerifyOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
